import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';

export type SaveConfirmModalProps = {
  open: boolean;
  note: string;
  noteError: string;
  changedFieldSummaries: string[];
  isSaving: boolean;
  onClose: () => void;
  onNoteChange: (value: string) => void;
  onConfirmSave: () => void;
};

export function SaveConfirmModal({
  open,
  note,
  noteError,
  changedFieldSummaries,
  isSaving,
  onClose,
  onNoteChange,
  onConfirmSave,
}: Readonly<SaveConfirmModalProps>) {
  return (
    <Dialog
      open={open}
      onClose={isSaving ? undefined : onClose}
      disableEscapeKeyDown={isSaving}
      maxWidth={false}
      fullWidth
      data-testid="policy-save-confirm-modal"
      sx={{
        '& .MuiDialog-paper': { maxWidth: 720 },
        '& .MuiDialogTitle-root + .MuiDialogContent-root': { pt: 3 },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 3,
          py: 2,
          borderBottom: '1px dashed',
          borderColor: 'divider',
        }}
      >
        Confirm save changes
        <IconButton
          disabled={isSaving}
          onClick={onClose}
          aria-label="close"
          data-testid="policy-save-confirm-close"
          size="small"
          sx={{ color: 'text.secondary' }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 3, pb: 0 }}>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          Review the detected field updates below before saving.
        </Typography>

        <Box
          data-testid="policy-save-system-summary"
          sx={{
            mb: 2,
            p: 1.5,
            borderRadius: 1,
            bgcolor: 'grey.100',
            maxHeight: 180,
            overflowY: 'auto',
          }}
        >
          {changedFieldSummaries.length > 0 ? (
            changedFieldSummaries.map((line, index) => (
              <Typography
                key={`${line}-${index}`}
                variant="body2"
                sx={{ mb: index === changedFieldSummaries.length - 1 ? 0 : 0.75 }}
              >
                {line}
              </Typography>
            ))
          ) : (
            <Typography variant="body2">No editable field changes were detected.</Typography>
          )}
        </Box>

        <TextField
          fullWidth
          multiline
          rows={4}
          label="Notes"
          value={note}
          onChange={(event) => onNoteChange(event.target.value)}
          error={Boolean(noteError)}
          helperText={noteError || 'Required'}
          data-testid="policy-save-note-input"
        />
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: 'space-between',
          px: 3,
          pb: 3,
          pt: 2,
          borderTop: '1px dashed',
          borderColor: 'divider',
        }}
      >
        <Button
          disabled={isSaving}
          onClick={onClose}
          variant="outlined"
          color="inherit"
          data-testid="policy-save-confirm-no-go-back"
        >
          No, go back
        </Button>
        <Button
          variant="contained"
          color="inherit"
          onClick={onConfirmSave}
          disabled={isSaving}
          data-testid="policy-save-confirm-yes-save"
          sx={{ bgcolor: 'grey.800', color: 'common.white', '&:hover': { bgcolor: 'grey.700' } }}
        >
          Yes, save changes
        </Button>
      </DialogActions>
    </Dialog>
  );
}
